import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, useTheme, useLang, API } from '../App';
import { LanguageSelector } from '../components/LanguageSelector';
import { Button } from '../components/ui/button';
import { LogoBrand, LogoFull } from '../components/Logo';
import axios from 'axios';
import { Mail, Lock, User, Sun, Moon, ArrowLeft, Eye, EyeOff, ShieldCheck, KeyRound, Copy, Download } from 'lucide-react';
import { toast } from 'sonner';

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const inputClass = "w-full pl-10 pr-4 py-2.5 bg-[#112C38] border border-[#285D71] rounded text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-[#45B08F] transition-colors";

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const routerLocation = useLocation();
  const { theme, toggleTheme } = useTheme();
  const { lang } = useLang();
  const isEn = lang !== 'fr';
  const isRegister = false;
  const [isForgot, setIsForgot] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  // 2FA state
  const [step, setStep] = useState('credentials'); // credentials | verify | setup | recovery
  const [pre2faToken, setPre2faToken] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [qrDataUri, setQrDataUri] = useState('');
  const [otpauthUri, setOtpauthUri] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [pendingSession, setPendingSession] = useState(null);
  const [useRecovery, setUseRecovery] = useState(false);

  React.useEffect(() => {
    if (user) navigate('/briefing', { replace: true });
  }, [user, navigate]);

  const startSetup = async (token) => {
    const { data } = await axios.post(`${API}/auth/2fa/setup-begin`, { pre_2fa_token: token });
    setQrDataUri(data.qr_data_uri);
    setOtpauthUri(data.otpauth_uri);
    setStep('setup');
  };

  const handleAuthResponse = async (data) => {
    if (data.requires_2fa) {
      setPre2faToken(data.pre_2fa_token);
      setTotpCode('');
      if (data.setup_required) {
        await startSetup(data.pre_2fa_token);
      } else {
        setStep('verify');
      }
    } else {
      login(data);
      navigate('/briefing', { replace: true });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isForgot) {
        await axios.post(`${API}/auth/forgot-password`, { email });
        setForgotSent(true);
      } else if (isRegister) {
        if (!name.trim()) { setError('Name is required'); setLoading(false); return; }
        if (password.length < 12 || !/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/\d/.test(password)) {
          setError(isEn ? 'Password must be at least 12 characters, with an uppercase, a lowercase and a digit' : 'Le mot de passe doit contenir au moins 12 caractères, une majuscule, une minuscule et un chiffre');
          setLoading(false);
          return;
        }
        const { data } = await axios.post(`${API}/auth/register`, { email, password, name });
        await handleAuthResponse(data);
      } else {
        const { data } = await axios.post(`${API}/auth/login`, { email, password });
        await handleAuthResponse(data);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/2fa/verify`, { pre_2fa_token: pre2faToken, code: totpCode.trim() });
      if (data.recovery_code_used) {
        toast.warning(isEn ? 'Recovery code used. It will no longer be valid.' : 'Code de récupération utilisé. Il ne sera plus valide.');
      }
      login(data);
      navigate('/briefing', { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      if (err.response?.status === 401 && String(err.response?.data?.detail || '').includes('expirée')) {
        setStep('credentials');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSetupConfirm = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/2fa/setup-confirm`, { pre_2fa_token: pre2faToken, code: totpCode.trim() });
      setRecoveryCodes(data.recovery_codes || []);
      setPendingSession(data);
      setStep('recovery');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      if (err.response?.status === 401 && String(err.response?.data?.detail || '').includes('expirée')) {
        setStep('credentials');
      }
    } finally {
      setLoading(false);
    }
  };

  const finishRecovery = () => {
    if (pendingSession) {
      login(pendingSession);
      navigate('/briefing', { replace: true });
    }
  };

  const copyRecovery = () => {
    navigator.clipboard.writeText(recoveryCodes.join('\n'));
    toast.success(isEn ? 'Codes copied to clipboard' : 'Codes copiés dans le presse-papiers');
  };

  const downloadRecovery = () => {
    const blob = new Blob([`${isEn ? 'IDFC recovery codes' : 'Codes de récupération IDFC'} — ${email}\n\n${recoveryCodes.join('\n')}\n`], { type: 'text/plain' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = isEn ? 'idfc-recovery-codes.txt' : 'idfc-codes-recuperation.txt';
    a.click();
  };

  const backToCredentials = () => {
    setStep('credentials');
    setError('');
    setTotpCode('');
    setPre2faToken('');
    setUseRecovery(false);
  };

  return (
    <div className="min-h-screen bg-[#112C38] flex flex-col">
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-[#285D71]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button onClick={() => navigate('/')} className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-testid="back-to-home">
              <ArrowLeft className="w-4 h-4 text-slate-400" />
              <LogoBrand size={36} />
            </button>
            <div className="flex items-center gap-1">
              <LanguageSelector tone="light" testId="login-lang-selector" />
            </div>
          </div>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-4 pt-20 pb-10">
        <div className="w-full max-w-md">
          <div className="bg-[#184252] border border-[#285D71] rounded-lg p-8">

            {step === 'verify' && (
              <div data-testid="totp-verify-step">
                <div className="text-center mb-6">
                  <div className="w-14 h-14 rounded-full bg-[#45B08F]/10 flex items-center justify-center mx-auto mb-4">
                    <ShieldCheck className="w-7 h-7 text-[#45B08F]" />
                  </div>
                  <h2 className="font-heading text-2xl font-medium text-white">{isEn ? '2FA Verification' : 'Vérification 2FA'}</h2>
                  <p className="text-sm text-slate-400 mt-1">
                    {useRecovery ? (isEn ? 'Enter one of your recovery codes' : 'Entrez un de vos codes de récupération') : (isEn ? 'Enter the 6-digit code from your authenticator app' : "Entrez le code à 6 chiffres de votre application d'authentification")}
                  </p>
                </div>
                {error && (
                  <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded text-sm text-red-400" data-testid="auth-error">{error}</div>
                )}
                <form onSubmit={handleVerify} className="space-y-4">
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <input
                      type="text"
                      inputMode={useRecovery ? 'text' : 'numeric'}
                      maxLength={useRecovery ? 32 : 6}
                      value={totpCode}
                      onChange={(e) => setTotpCode(useRecovery ? e.target.value : e.target.value.replace(/\D/g, ''))}
                      placeholder={useRecovery ? 'XXXX-XXXX-XXXX' : '000000'}
                      autoFocus
                      required
                      className={`${inputClass} font-mono tracking-[0.3em] text-center`}
                      data-testid="totp-code-input"
                    />
                  </div>
                  <Button type="submit" disabled={loading || totpCode.length < 6} className="w-full bg-[#45B08F] hover:bg-[#3A9679] text-white font-medium py-2.5" data-testid="totp-verify-btn">
                    {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : (isEn ? 'Verify' : 'Vérifier')}
                  </Button>
                </form>
                <div className="mt-4 text-center space-y-2">
                  <button onClick={() => { setUseRecovery(!useRecovery); setTotpCode(''); setError(''); }} className="block w-full text-sm text-[#F5C544] hover:text-[#e0b53a] transition-colors" data-testid="use-recovery-link">
                    {useRecovery ? (isEn ? 'Use authenticator code' : "Utiliser le code de l'application") : (isEn ? 'Use a recovery code' : 'Utiliser un code de récupération')}
                  </button>
                  <button onClick={backToCredentials} className="block w-full text-sm text-slate-500 hover:text-slate-300 transition-colors" data-testid="back-to-credentials">
                    {isEn ? 'Back to Sign In' : 'Retour à la connexion'}
                  </button>
                </div>
              </div>
            )}

            {step === 'setup' && (
              <div data-testid="totp-setup-step">
                <div className="text-center mb-6">
                  <div className="w-14 h-14 rounded-full bg-[#F5C544]/10 flex items-center justify-center mx-auto mb-4">
                    <ShieldCheck className="w-7 h-7 text-[#F5C544]" />
                  </div>
                  <h2 className="font-heading text-2xl font-medium text-white">{isEn ? 'Mandatory 2FA Activation' : 'Activation 2FA obligatoire'}</h2>
                  <p className="text-sm text-slate-400 mt-1">
                    {isEn ? 'Banking compliance: scan this QR code with Google Authenticator, Microsoft Authenticator or a compatible app.' : 'Conformité bancaire : scannez ce QR code avec Google Authenticator, Microsoft Authenticator ou une application compatible.'}
                  </p>
                </div>
                {error && (
                  <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded text-sm text-red-400" data-testid="auth-error">{error}</div>
                )}
                {qrDataUri && (
                  <div className="bg-white rounded-lg p-3 w-48 mx-auto mb-4">
                    <img src={qrDataUri} alt="QR code 2FA" className="w-full" data-testid="totp-qr-img" />
                  </div>
                )}
                {otpauthUri && (
                  <details className="mb-4">
                    <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-300">{isEn ? 'Cannot scan? Manual entry' : 'Impossible de scanner ? Saisie manuelle'}</summary>
                    <p className="text-[11px] font-mono text-slate-400 break-all mt-2 p-2 bg-[#112C38] rounded" data-testid="totp-manual-secret">
                      {(otpauthUri.match(/secret=([A-Z2-7]+)/) || [])[1] || otpauthUri}
                    </p>
                  </details>
                )}
                <form onSubmit={handleSetupConfirm} className="space-y-4">
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                      placeholder={isEn ? '6-digit code' : 'Code à 6 chiffres'}
                      required
                      className={`${inputClass} font-mono tracking-[0.3em] text-center`}
                      data-testid="setup-code-input"
                    />
                  </div>
                  <Button type="submit" disabled={loading || totpCode.length !== 6} className="w-full bg-[#45B08F] hover:bg-[#3A9679] text-white font-medium py-2.5" data-testid="setup-confirm-btn">
                    {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : (isEn ? 'Enable 2FA' : 'Activer la 2FA')}
                  </Button>
                </form>
                <button onClick={backToCredentials} className="block w-full text-sm text-slate-500 hover:text-slate-300 transition-colors mt-4" data-testid="back-to-credentials">
                  {isEn ? 'Back to Sign In' : 'Retour à la connexion'}
                </button>
              </div>
            )}

            {step === 'recovery' && (
              <div data-testid="recovery-codes-step">
                <div className="text-center mb-6">
                  <div className="w-14 h-14 rounded-full bg-[#45B08F]/10 flex items-center justify-center mx-auto mb-4">
                    <KeyRound className="w-7 h-7 text-[#45B08F]" />
                  </div>
                  <h2 className="font-heading text-2xl font-medium text-white">{isEn ? 'Recovery codes' : 'Codes de récupération'}</h2>
                  <p className="text-sm text-slate-400 mt-1">
                    {isEn ? 'Keep these codes in a safe place. Each code can be used once if you lose access to your app.' : "Conservez ces codes en lieu sûr. Chaque code est utilisable une seule fois si vous perdez l'accès à votre application."}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-2 bg-[#112C38] border border-[#285D71] rounded p-4 mb-4" data-testid="recovery-codes-list">
                  {recoveryCodes.map((c) => (
                    <span key={c} className="font-mono text-xs text-[#F5C544]">{c}</span>
                  ))}
                </div>
                <div className="flex gap-2 mb-4">
                  <Button type="button" variant="outline" onClick={copyRecovery} className="flex-1 border-[#285D71] text-slate-300" data-testid="copy-recovery-btn">
                    <Copy className="w-4 h-4 mr-2" /> {isEn ? 'Copy' : 'Copier'}
                  </Button>
                  <Button type="button" variant="outline" onClick={downloadRecovery} className="flex-1 border-[#285D71] text-slate-300" data-testid="download-recovery-btn">
                    <Download className="w-4 h-4 mr-2" /> {isEn ? 'Download' : 'Télécharger'}
                  </Button>
                </div>
                <Button onClick={finishRecovery} className="w-full bg-[#45B08F] hover:bg-[#3A9679] text-white font-medium py-2.5" data-testid="recovery-continue-btn">
                  {isEn ? "I've saved my codes — Continue" : "J'ai sauvegardé mes codes — Continuer"}
                </Button>
              </div>
            )}

            {step === 'credentials' && (
            <>
            <div className="text-center mb-8">
              <LogoFull className="h-12 mx-auto mb-4 shadow-lg" data-testid="login-logo" />
              <h2 className="font-heading text-2xl font-medium text-white">
                {isForgot ? (isEn ? 'Reset Password' : 'Réinitialiser le mot de passe') : isRegister ? (isEn ? 'Create Account' : 'Créer un compte') : (isEn ? 'Welcome Back' : 'Bon retour')}
              </h2>
              <p className="text-sm text-slate-400 mt-1">
                {isForgot ? (isEn ? 'Enter your email to receive a reset link' : 'Entrez votre email pour recevoir un lien de réinitialisation') : isRegister ? (isEn ? 'Join the co-financing platform' : 'Rejoignez la plateforme de cofinancement') : (isEn ? 'Sign in to your account' : 'Connectez-vous à votre compte')}
              </p>
            </div>

            {forgotSent ? (
              <div className="text-center py-4">
                <div className="w-14 h-14 rounded-full bg-[#45B08F]/10 flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-7 h-7 text-[#45B08F]" />
                </div>
                <h3 className="text-lg text-white font-medium mb-2">{isEn ? 'Check your email' : 'Vérifiez votre email'}</h3>
                <p className="text-sm text-slate-400 mb-6">{isEn ? 'If an account exists with this email, you will receive a password reset link.' : 'Si un compte existe avec cet email, vous recevrez un lien de réinitialisation.'}</p>
                <button
                  onClick={() => { setIsForgot(false); setForgotSent(false); setError(''); }}
                  className="text-sm text-[#45B08F] hover:text-[#3A9679] transition-colors"
                  data-testid="back-to-login-link"
                >
                  {isEn ? 'Back to Sign In' : 'Retour à la connexion'}
                </button>
              </div>
            ) : (
            <>
            {routerLocation.state?.reason === 'idle' && (
              <div className="mb-4 p-3 rounded border border-[#F5C544]/40 bg-[#F5C544]/10 text-sm text-[#F5C544]" data-testid="idle-logout-notice">
                {isEn ? 'You were signed out after 30 minutes of inactivity. Please sign in again.' : 'Vous avez été déconnecté après 30 minutes d’inactivité. Veuillez vous reconnecter.'}
              </div>
            )}
            {error && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded text-sm text-red-400" data-testid="auth-error">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {isRegister && !isForgot && (
                <div>
                  <label className="block text-xs text-slate-400 mb-1.5 uppercase tracking-wider">Full Name</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder={isEn ? 'Your full name' : 'Votre nom complet'}
                      className={inputClass}
                      data-testid="register-name-input"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs text-slate-400 mb-1.5 uppercase tracking-wider">{isEn ? 'Email' : 'Email'}</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={isEn ? 'you@institution.com' : 'vous@institution.com'}
                    required
                    className={inputClass}
                    data-testid="email-input"
                  />
                </div>
              </div>

              {!isForgot && (
              <div>
                <label className="block text-xs text-slate-400 mb-1.5 uppercase tracking-wider">{isEn ? 'Password' : 'Mot de passe'}</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={isRegister ? (isEn ? 'Min. 12 characters, 1 uppercase, 1 digit' : 'Min. 12 caractères, 1 majuscule, 1 chiffre') : (isEn ? 'Your password' : 'Votre mot de passe')}
                    required
                    className="w-full pl-10 pr-10 py-2.5 bg-[#112C38] border border-[#285D71] rounded text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-[#45B08F] transition-colors"
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#45B08F] hover:bg-[#3A9679] text-white font-medium py-2.5"
                data-testid="auth-submit-button"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  isForgot ? (isEn ? 'Send Reset Link' : 'Envoyer le lien') : isRegister ? (isEn ? 'Create Account' : 'Créer un compte') : (isEn ? 'Sign In' : 'Se connecter')
                )}
              </Button>
            </form>

            <div className="mt-6 flex items-center justify-center gap-2 text-xs text-slate-500">
              <ShieldCheck className="w-3.5 h-3.5 text-[#45B08F]" />
              {isEn ? 'Two-factor authentication required' : 'Authentification à deux facteurs obligatoire'}
            </div>

            <div className="mt-4 text-center space-y-2">
              {!isRegister && !isForgot && (
                <button
                  onClick={() => { setIsForgot(true); setError(''); }}
                  className="block w-full text-sm text-slate-500 hover:text-slate-300 transition-colors"
                  data-testid="forgot-password-link"
                >
                  {isEn ? 'Forgot your password?' : 'Mot de passe oublié ?'}
                </button>
              )}
              {isForgot ? (
                <button
                  onClick={() => { setIsForgot(false); setError(''); setForgotSent(false); }}
                  className="block w-full text-sm text-[#45B08F] hover:text-[#3A9679] transition-colors"
                  data-testid="toggle-auth-mode"
                >
                  {isEn ? 'Back to Sign In' : 'Retour à la connexion'}
                </button>
              ) : (
                <p className="text-[11px] text-slate-500 flex items-center justify-center gap-1" data-testid="invitation-only-notice">
                  <ShieldCheck className="w-3 h-3" /> {isEn ? 'Access by administrator invitation only' : "Accès sur invitation de l'administrateur uniquement"}
                </p>
              )}
            </div>
            </>
            )}
            </>
            )}
          </div>

          <p className="text-center text-xs text-slate-600 mt-6">
            IDFC — International Development Finance Club
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
